import {
  Component,
  OnInit,
  Injectable,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  AfterContentChecked,
  AfterViewChecked
} from "@angular/core";
import {
  SelectionChange,
  CollectionViewer,
  SelectionModel
} from "@angular/cdk/collections";
import { merge, Observable, BehaviorSubject } from "rxjs";
import { map } from "rxjs/operators";
import { FlatTreeControl } from "@angular/cdk/tree";
import { StrutturaVO } from "@app/shared/vo/strutturaVO";
import { OrganoService } from "@app/shared/services/organo.service";

/** Flat node with expandable and level information */
export class DynamicFlatNode {
  constructor(
    public item: StrutturaVO,
    public level = 1,
    public expandable = false,
    public isLoading = false
  ) {}
}

/**
 * Database for dynamic data. When expanding a node in the tree, the data source will need to fetch
 * the descendants data from the database.
 */

@Injectable()
export class DynamicDatabase {
  constructor(
    private organoSerivce: OrganoService
  ) {}
  dataMap = new Map<String, StrutturaVO[]>([]);

  /** Initial data from database */
  initialData(rootLevelNodes: StrutturaVO[], treeControl: FlatTreeControl<DynamicFlatNode>): DynamicFlatNode[] {
    rootLevelNodes.forEach(node => this.initAlbero(node, treeControl));
    return rootLevelNodes.map(name => new DynamicFlatNode(name, 0, true));
  }

  initAlbero(nodoPadre: StrutturaVO, treeControl: FlatTreeControl<DynamicFlatNode>) {
    if (nodoPadre != null) {
      this.organoSerivce.getAlberoCmu(nodoPadre.id).subscribe(mappa => {
        nodoPadre.children = mappa.size;
        mappa.forEach((value, key) => this.dataMap.set(key, value));
        treeControl.expandAll();
      });
    }
  }

  getChildren(node: StrutturaVO): StrutturaVO[] | undefined {
    return this.dataMap.get(node.id);
  }

  isExpandable(node: StrutturaVO): boolean {
    return this.dataMap.has(node.id);
  }
}
/**
 * File database, it can build a tree structured Json object from string.
 * Each node in Json object represents a file or a directory. For a file, it has filename and type.
 * For a directory, it has filename and children (a list of files or directories).
 * The input will be a json object string, and the output is a list of `FileNode` with nested
 * structure.
 */
@Injectable()
export class DynamicDataSource {
  dataChange = new BehaviorSubject<DynamicFlatNode[]>([]);

  get data(): DynamicFlatNode[] {
    return this.dataChange.value;
  }
  set data(value: DynamicFlatNode[]) {
    this.treeControl.dataNodes = value;
    this.dataChange.next(value);
  }

  constructor(
    private treeControl: FlatTreeControl<DynamicFlatNode>,
    private database: DynamicDatabase
  ) {}

  connect(collectionViewer: CollectionViewer): Observable<DynamicFlatNode[]> {
    // tslint:disable-next-line:no-non-null-assertion
    this.treeControl.expansionModel.onChange!.subscribe(change => {
      if (
        (change as SelectionChange<DynamicFlatNode>).added ||
        (change as SelectionChange<DynamicFlatNode>).removed
      ) {
        this.handleTreeControl(change as SelectionChange<DynamicFlatNode>);
      }
    });

    return merge(collectionViewer.viewChange, this.dataChange).pipe(
      map(() => this.data)
    );
  }

  /** Handle expand/collapse behaviors */
  handleTreeControl(change: SelectionChange<DynamicFlatNode>) {
    if (change.added) {
      change.added.forEach(node => this.toggleNode(node, true));
    }
    if (change.removed) {
      change.removed
        .slice()
        .reverse()
        .forEach(node => this.toggleNode(node, false));
    }
  }

  /**
   * Toggle the node, remove from display list
   */
  toggleNode(node: DynamicFlatNode, expand: boolean) {
    const children = this.database.getChildren(node.item);
    const index = this.data.indexOf(node);
    if (!children || index < 0) {
      // If no children, or cannot find the node, no op
      console.log("NO OP!!!");
      return;
    }

    node.isLoading = true;

    if (expand) {
      const nodes = children.map(
        name =>
          new DynamicFlatNode(
            name,
            node.level + 1,
            this.database.isExpandable(name)
          )
      );
      this.data.splice(index + 1, 0, ...nodes);
    } else {
      let count = 0;
      for (
        let i = index + 1;
        i < this.data.length && this.data[i].level > node.level;
        i++, count++
      ) {}
      this.data.splice(index + 1, count);
    }

    // notify the change
    this.dataChange.next(this.data);
    node.isLoading = false;
  }
}

@Component({
  selector: "app-albero-settori",
  templateUrl: "./albero-settori.component.html",
  providers: [DynamicDatabase]
})
export class AlberoSettoriComponent implements OnInit, OnChanges {
  @Input()
  strutturaRadice: StrutturaVO;
  @Input()
  settoriSelezionati: string[] = [];
  @Output()
  cmuSelected: EventEmitter<String[]> = new EventEmitter<String[]>();

  checklistSelection = new SelectionModel<DynamicFlatNode>(true /* multiple */);
  treeControl: FlatTreeControl<DynamicFlatNode>;
  dataSource: DynamicDataSource;
  getLevel = (node: DynamicFlatNode) => node.level;
  isExpandable = (node: DynamicFlatNode) => node.expandable;
  hasChild = (_: number, _nodeData: DynamicFlatNode) => _nodeData.expandable;

  constructor(private database: DynamicDatabase) {
    this.treeControl = new FlatTreeControl<DynamicFlatNode>(
      this.getLevel,
      this.isExpandable
    );
    this.dataSource = new DynamicDataSource(this.treeControl, database);
  }

  todoItemSelectionToggle(node: DynamicFlatNode): void {
    this.checklistSelection.toggle(node);
    this.emettiSettoriSelezionati(this.checklistSelection);
  }

  enableCheck(codice: string): boolean {
    return (
      codice !== this.strutturaRadice.id && !this.isSettoreGiaAttribuito(codice)
    );
  }

  emettiSettoriSelezionati(
    checklistSelection: SelectionModel<DynamicFlatNode>
  ) {
    const arraySettori: string[] = [];
    this.checklistSelection.selected.forEach(value =>
      arraySettori.push(value.item.id)
    );
    this.cmuSelected.emit(arraySettori);
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log("albero-settori - changes : " + JSON.stringify(changes));
    if (
      this.strutturaRadice !== undefined &&
      this.strutturaRadice.id !== undefined
    ) {
      if (changes.strutturaRadice) {
      console.log(
        "albero-settori - recupero albero con radice: ",
        this.strutturaRadice.id
      );
      this.strutturaRadice.output = this.strutturaRadice.id;
      const rootLevelNodes: StrutturaVO[] = [this.strutturaRadice];
      this.dataSource.data = this.database.initialData(rootLevelNodes, this.treeControl);
      }
    }
  }

  ngOnInit() {}

  private isSettoreGiaAttribuito(codice: string): boolean {
    if (this.settoriSelezionati) {
      return this.settoriSelezionati.filter(value => value === codice).length > 0;
    } else {
      return false;
    }
  }
}
